import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { api, formatApiError, structuredApiError } from "@/lib/api";
import { useMasters, nameOf, sizeOf } from "@/lib/masters";
import { PageHeader, Card, Btn, Input, Select, Field, Chip } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { Plus, X, CheckCircle, WarningCircle, XCircle, Info, FilePdf, FileXls } from "@phosphor-icons/react";
import { formatISTDateTime, istDateString } from "@/lib/date";
import { useAuth } from "@/lib/auth";

const JOB_WORKSHOPS = ["AMSKY", "CRON", "KODAK"];

const STATUS_TONE = {
  ok: "good",
  ambiguous: "warn",
  invalid: "bad",
  not_found: "bad",
  missing: "neutral",
};
const STATUS_LABEL = {
  ok: "OK",
  ambiguous: "Confirm",
  invalid: "Invalid",
  not_found: "Not in master",
  missing: "Missing",
};

const FIELD_LABELS = {
  job_name: "Job Name",
  client: "Client",
  dpi: "DPI",
  plate_size: "Plate Size",
  set_colour: "Set/Colour",
  operator: "Operator",
  machine: "Machine",
  remark: "Remark",
};

function ParseReport({ parse, onQuickAddClient, onQuickAddPlateSize, onQuickAddDpi, onQuickAddOperator }) {
  if (!parse) return null;
  const { fields, overall, duplicate } = parse;
  const order = ["job_name", "client", "dpi", "plate_size", "set_colour", "operator", "machine", "remark"];
  return (
    <div className="border border-zinc-200 bg-zinc-50/50 rounded-sm p-3 space-y-2" data-testid="parse-report">
      <div className="flex items-center gap-2">
        {overall.ok ? (
          <><CheckCircle size={16} weight="fill" className="text-emerald-600" />
            <div className="text-sm font-semibold text-emerald-800">Filename parses cleanly — review and submit.</div></>
        ) : (
          <><XCircle size={16} weight="fill" className="text-red-600" />
            <div className="text-sm font-semibold text-red-800">Filename has {overall.errors.length} problem{overall.errors.length===1?"":"s"} that must be fixed before saving.</div></>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {order.map((f) => {
          const v = fields[f];
          if (!v) return null;
          const tone = STATUS_TONE[v.status] || "neutral";
          const isClientNotFound = f === "client" && v.status === "not_found" && v.raw;
          const isPlateNotFound = f === "plate_size" && v.status === "not_found" && v.value && /^\d+X\d+$/.test(v.value);
          const isDpiNotFound = f === "dpi" && v.status === "not_found" && v.value;
          const isOperatorNotFound = f === "operator" && v.status === "not_found" && v.raw;
          return (
            <div key={f} className="border border-zinc-200 bg-white rounded-sm px-2 py-1.5" data-testid={`parse-field-${f}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{FIELD_LABELS[f]}</div>
                <Chip tone={tone}>{STATUS_LABEL[v.status] || v.status}</Chip>
              </div>
              <div className="text-xs font-mono truncate mt-0.5">{v.value || v.raw || <span className="text-zinc-400">—</span>}</div>
              {v.message && <div className="text-[11px] text-red-700 mt-1 leading-snug">{v.message}</div>}
              {isClientNotFound && (
                <button
                  type="button"
                  onClick={() => onQuickAddClient(v.raw)}
                  data-testid="parse-quickadd-client"
                  className="mt-1.5 text-[11px] font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
                >
                  + Add "{v.raw}" to Client Master
                </button>
              )}
              {isPlateNotFound && (
                <button
                  type="button"
                  onClick={() => onQuickAddPlateSize(v.value)}
                  data-testid="parse-quickadd-plate-size"
                  className="mt-1.5 text-[11px] font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
                >
                  + Add "{v.value}" to Plate Size Master
                </button>
              )}
              {isDpiNotFound && (
                <button
                  type="button"
                  onClick={() => onQuickAddDpi(v.value)}
                  data-testid="parse-quickadd-dpi"
                  className="mt-1.5 text-[11px] font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
                >
                  + Add DPI "{v.value}" to Master
                </button>
              )}
              {isOperatorNotFound && (
                <button
                  type="button"
                  onClick={() => onQuickAddOperator(v.raw)}
                  data-testid="parse-quickadd-operator"
                  className="mt-1.5 text-[11px] font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
                >
                  + Add Operator "{v.raw}" to Master
                </button>
              )}
            </div>
          );
        })}
      </div>
      {duplicate && (
        <div className="flex items-start gap-2 text-[12px] text-amber-900 bg-amber-50 border border-amber-300 rounded-sm px-2 py-1.5" data-testid="parse-duplicate-warning">
          <WarningCircle size={14} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Possible duplicate.</div>
            An identical job name for this client already exists: <span className="font-mono font-bold">{duplicate.job_number}</span> ({duplicate.job_date}, {duplicate.final_plate_qty} plates). Verify before submitting.
          </div>
        </div>
      )}
      {overall.warnings.length > 0 && !duplicate && (
        <div className="flex items-start gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
          <WarningCircle size={14} weight="fill" className="text-amber-600 mt-0.5" />
          <div>{overall.warnings.join(" · ")}</div>
        </div>
      )}
    </div>
  );
}

export default function Jobs() {
  const { user } = useAuth();
  const m = useMasters();
  const canManageAllWorkshops = ["super_admin", "admin", "manager"].includes(user?.role);
  const assignedWorkshop = user?.assigned_workshop || "AMSKY";
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState({
    client_id: "", workshop: canManageAllWorkshops ? "" : assignedWorkshop, baking: "",
    from_date: istDateString(), to_date: istDateString(),
  });
  const [hideCancelled, setHideCancelled] = useState(true);
  const [exporting, setExporting] = useState("");
  const [stockHint, setStockHint] = useState(null);
  const [parse, setParse] = useState(null);
  const [fieldError, setFieldError] = useState(null); // { field, problem, resolution }
  const [clientSizeHistory, setClientSizeHistory] = useState({ client_id: "", loading: false, sizes: [] });
  const [confirmedNewSize, setConfirmedNewSize] = useState("");
  const parseTimer = useRef(null);
  const clientSizeHistoryRequest = useRef(0);
  const jobNameEdited = useRef(false);
  const today = istDateString();

  const initial = {
    job_date: today, job_name: "", baking: false, file_name: "", client_id: "", dpi: "",
    plate_size_id: "", sets: 1, colours: 1, plate_qty_override: "", override_reason: "",
    operator_id: "", machine_id: "", production_unit_id: "", location_id: "", remarks: "",
    workshop: user?.assigned_workshop || "AMSKY", status: "submitted", thumbnail_token: "",
  };
  const [form, setForm] = useState(initial);
  const clientName = m.clients.find(c => c.id === form.client_id)?.name || "this client";
  const historyApplies = clientSizeHistory.client_id === form.client_id && clientSizeHistory.sizes.length > 0;
  const usualSizeIds = historyApplies ? clientSizeHistory.sizes.map(item => item.plate_size_id) : [];
  const selectedPlateIsNew = Boolean(
    form.plate_size_id && historyApplies && !usualSizeIds.includes(form.plate_size_id),
  );
  const [thumbPreview, setThumbPreview] = useState(null);   // data URL for the uploaded PDF's first-page thumbnail
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/jobs", { params });
    setJobs(data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const visibleJobs = useMemo(
    () => hideCancelled ? jobs.filter(j => !j.cancelled) : jobs,
    [jobs, hideCancelled],
  );

  const exportJobs = async (format) => {
    if (!filter.from_date || !filter.to_date) {
      return toast.error("Select both From and To dates before exporting.");
    }
    setExporting(format);
    try {
      const params = { ...filter, exclude_cancelled: hideCancelled };
      Object.keys(params).forEach(k => {
        if (params[k] === "" || params[k] == null) delete params[k];
      });
      const res = await api.get(`/jobs/export.${format}`, { params, responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fallbackName = `job-sheet_${filter.from_date}_to_${filter.to_date}.${format}`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = match?.[1] || fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Job Sheet ${format.toUpperCase()} exported.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting("");
    }
  };

  const finalQty = form.plate_qty_override
    ? parseInt(form.plate_qty_override) || 0
    : (parseInt(form.sets) || 0) * (parseInt(form.colours) || 0);

  useEffect(() => {
    (async () => {
      if (form.machine_id && form.plate_size_id && form.location_id) {
        try {
          const { data } = await api.get("/inventory/stock_at", { params: { machine_id: form.machine_id, plate_size_id: form.plate_size_id, location_id: form.location_id } });
          setStockHint(data.stock);
        } catch (err) {
          // Non-fatal: display "-" instead of a hard failure so the user can still submit.
          console.warn("stock_at fetch failed:", err?.message || err);
          setStockHint(null);
        }
      } else setStockHint(null);
    })();
  }, [form.machine_id, form.plate_size_id, form.location_id]);

  const loadClientSizeHistory = async (clientId, { prefill = true } = {}) => {
    const requestId = ++clientSizeHistoryRequest.current;
    if (!clientId) {
      setClientSizeHistory({ client_id: "", loading: false, sizes: [] });
      return;
    }
    setClientSizeHistory({ client_id: clientId, loading: true, sizes: [] });
    try {
      const { data } = await api.get(`/jobs/client-size-history/${clientId}`, { params: { limit: 4 } });
      if (requestId !== clientSizeHistoryRequest.current) return;
      const sizes = data.suggestions || [];
      setClientSizeHistory({ client_id: clientId, loading: false, sizes });
      if (prefill && sizes[0]?.plate_size_id) {
        setForm((current) => (
          current.client_id === clientId && !current.plate_size_id
            ? { ...current, plate_size_id: sizes[0].plate_size_id }
            : current
        ));
      }
    } catch (err) {
      if (requestId !== clientSizeHistoryRequest.current) return;
      console.warn("client size history fetch failed:", err?.message || err);
      setClientSizeHistory({ client_id: clientId, loading: false, sizes: [] });
    }
  };

  const onClientChange = (clientId) => {
    setConfirmedNewSize("");
    setForm((current) => ({ ...current, client_id: clientId, plate_size_id: "" }));
    loadClientSizeHistory(clientId, { prefill: true });
  };

  const onPlateSizeChange = (plateSizeId) => {
    if (!plateSizeId) {
      setConfirmedNewSize("");
      setForm((current) => ({ ...current, plate_size_id: "" }));
      return;
    }
    const isNewForClient = historyApplies && !usualSizeIds.includes(plateSizeId);
    if (isNewForClient) {
      const sizeName = sizeOf(m.plate_sizes, plateSizeId);
      const proceed = window.confirm(
        `${sizeName} is a new or uncommon plate size for ${clientName}. Do you want to continue with this size?`,
      );
      if (!proceed) return;
      setConfirmedNewSize(`${form.client_id}:${plateSizeId}`);
    } else {
      setConfirmedNewSize("");
    }
    setForm((current) => ({ ...current, plate_size_id: plateSizeId }));
  };

  // Debounced filename parse via backend
  const doParse = async (fn) => {
    if (!fn || fn.length < 3) { setParse(null); return; }
    try {
      const { data } = await api.post("/jobs/parse_filename", { filename: fn });
      setParse(data);
      const parsedClientId = data.fields?.client?.id;
      const parsedPlateSizeId = data.fields?.plate_size?.id;
      if (parsedClientId && !form.client_id) {
        setConfirmedNewSize("");
        loadClientSizeHistory(parsedClientId, { prefill: !parsedPlateSizeId });
      }
      // auto-apply ok/ambiguous suggestions to form (never overwrite explicit user choice unless field is empty)
      setForm((f) => {
        const next = { ...f };
        const F = data.fields;
        if (F.job_name?.status === "ok" && !jobNameEdited.current) next.job_name = F.job_name.value;
        if (F.client?.id && !f.client_id) next.client_id = F.client.id;
        if (F.dpi?.status === "ok" && !f.dpi) next.dpi = F.dpi.value;
        if (F.plate_size?.id && !f.plate_size_id) next.plate_size_id = F.plate_size.id;
        if (F.set_colour?.status === "ok") {
          if (parseInt(f.sets) === 1 && parseInt(f.colours) === 1) {
            next.sets = F.set_colour.sets;
            next.colours = F.set_colour.colours;
          }
        }
        if (F.operator?.id && !f.operator_id) next.operator_id = F.operator.id;
        // Auto-derive workshop (location) from the machine parsed in filename — workshop == machine name == PU name
        if (F.machine?.value && !f.location_id) {
          const parsedName = F.machine.value.toLowerCase();
          const loc = m.locations.find(l => l.name.toLowerCase() === parsedName);
          if (loc) {
            next.location_id = loc.id;
            next.machine_id = F.machine.id || (m.machines.find(x => x.id === F.machine.id) ? F.machine.id : "");
            const pu = m.production_units.find(x => x.name.toLowerCase() === parsedName);
            if (pu) next.production_unit_id = pu.id;
          }
        }
        if (F.remark?.value && !f.remarks) next.remarks = F.remark.value;
        return next;
      });
    } catch (err) {
      // Non-fatal: filename parsing is a nicety; user can still fill the form manually.
      console.warn("parse_filename failed:", err?.message || err);
    }
  };

  const onFilenameChange = (v) => {
    setForm((f) => ({ ...f, file_name: v }));
    if (parseTimer.current) clearTimeout(parseTimer.current);
    parseTimer.current = setTimeout(() => doParse(v), 300);
  };

  const openForm = () => {
    jobNameEdited.current = false;
    clientSizeHistoryRequest.current += 1;
    setClientSizeHistory({ client_id: "", loading: false, sizes: [] });
    setConfirmedNewSize("");
    const assigned = user?.assigned_workshop || "AMSKY";
    const loc = m.locations.find(x => x.name?.toUpperCase() === assigned);
    const mc = m.machines.find(x => x.name?.toUpperCase() === assigned || x.code?.toUpperCase() === assigned);
    const pu = m.production_units.find(x => x.name?.toUpperCase() === assigned);
    setForm({ ...initial, workshop: assigned, location_id: loc?.id || "", machine_id: mc?.id || "", production_unit_id: pu?.id || "" });
    setParse(null);
    setFieldError(null);
    setThumbPreview(null);
    setShowForm(true);
  };

  // Upload a PDF, render page-1 to a JPG thumbnail on the server, auto-fill filename & parse.
  const onPdfUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("Please upload a .pdf file.");
    }
    setUploadingPdf(true);
    jobNameEdited.current = false;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/jobs/upload_pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Save token + preview URL for the dialog
      setForm((f) => ({ ...f, file_name: data.original_filename, thumbnail_token: data.thumbnail_token }));
      setThumbPreview(data.thumbnail_url);
      // Trigger a parse via the file_name change path (mirror onFilenameChange flow)
      if (parseTimer.current) clearTimeout(parseTimer.current);
      parseTimer.current = setTimeout(() => doParse(data.original_filename), 100);
      toast.success("PDF uploaded — thumbnail generated.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingPdf(false); }
  };

  const quickAddClient = async (name) => {
    if (!name || !window.confirm(`Add "${name}" to Client Master?`)) return;
    try {
      const { data } = await api.post("/masters/clients", { name, active: true, credit_limit: 0 });
      toast.success(`Client "${data.name}" created`);
      await m.reload();
      // reflect in form + re-parse
      setForm((f) => ({ ...f, client_id: data.id }));
      setClientSizeHistory({ client_id: data.id, loading: false, sizes: [] });
      setConfirmedNewSize("");
      if (form.file_name) doParse(form.file_name);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quickAddPlateSize = async (canonical) => {
    // canonical is like "482X620"
    const match = String(canonical || "").match(/^(\d+)X(\d+)$/);
    if (!match) return toast.error(`"${canonical}" is not a valid plate size (expected WxH e.g. 482X620).`);
    const w = parseInt(match[1]); const h = parseInt(match[2]);
    if (!window.confirm(`Add plate size "${w}X${h}" to Plate Size Master?`)) return;
    try {
      const { data } = await api.post("/masters/plate_sizes", { width: w, height: h, unit: "mm", active: true, min_stock: 0 });
      toast.success(`Plate size "${data.canonical}" created`);
      await m.reload();
      setForm((f) => ({ ...f, plate_size_id: data.id }));
      if (form.file_name) doParse(form.file_name);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quickAddDpi = async (value) => {
    const v = parseInt(value);
    if (!v || v <= 0) return toast.error(`"${value}" is not a valid DPI.`);
    if (!window.confirm(`Add DPI ${v} to Master?`)) return;
    try {
      await api.post("/masters/dpis", { value: v, active: true });
      toast.success(`DPI ${v} created`);
      await m.reload();
      setForm((f) => ({ ...f, dpi: String(v) }));
      if (form.file_name) doParse(form.file_name);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quickAddOperator = async (name) => {
    if (!name || !window.confirm(`Add operator "${name}" to Master?`)) return;
    try {
      const { data } = await api.post("/masters/operators", { name, active: true });
      toast.success(`Operator "${data.name}" created`);
      await m.reload();
      setForm((f) => ({ ...f, operator_id: data.id }));
      if (form.file_name) doParse(form.file_name);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async (status = "submitted") => {
    setFieldError(null);
    if (
      selectedPlateIsNew
      && confirmedNewSize !== `${form.client_id}:${form.plate_size_id}`
    ) {
      const sizeName = sizeOf(m.plate_sizes, form.plate_size_id);
      const proceed = window.confirm(
        `${sizeName} is not among the four usual plate sizes for ${clientName}. Do you want to continue?`,
      );
      if (!proceed) return;
      setConfirmedNewSize(`${form.client_id}:${form.plate_size_id}`);
    }
    try {
      const body = { ...form, status };
      body.sets = parseInt(body.sets); body.colours = parseInt(body.colours);
      body.plate_qty_override = body.plate_qty_override ? parseInt(body.plate_qty_override) : null;
      if (!body.plate_qty_override) delete body.plate_qty_override;
      if (!body.override_reason) delete body.override_reason;
      if (!body.operator_id) delete body.operator_id;
      const { data } = await api.post("/jobs", body);
      toast.success(status === "draft"
        ? `Draft ${data.job_number} saved — no stock deducted yet.`
        : `Job ${data.job_number} created — stock deducted.`);
      setShowForm(false);
      load();
    } catch (e) {
      const s = structuredApiError(e);
      if (s) {
        setFieldError(s);
        toast.error(s.problem);
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const promote = async (id) => {
    if (!window.confirm("Promote draft to submitted? This will deduct plates from stock.")) return;
    try {
      await api.post(`/jobs/${id}/promote`);
      toast.success("Draft promoted — stock deducted.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [historyModal, setHistoryModal] = useState(null);
  const [editJob, setEditJob] = useState(null);   // job being edited
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (j) => {
    setEditJob(j);
    setEditForm({
      machine_id: j.machine_id, plate_size_id: j.plate_size_id, location_id: j.location_id,
      workshop: j.workshop || "AMSKY",
      sets: j.sets, colours: j.colours,
      plate_qty_override: j.plate_qty_override || "",
      job_date: j.job_date, remarks: j.remarks || "",
      baking: Boolean(j.baking),
      reason: "",
    });
  };

  const saveEdit = async () => {
    if (!editForm.reason || editForm.reason.trim().length < 3) {
      return toast.error("Reason (min 3 chars) is required for a correction.");
    }
    setSavingEdit(true);
    try {
      const body = { ...editForm };
      body.sets = parseInt(body.sets); body.colours = parseInt(body.colours);
      body.plate_qty_override = body.plate_qty_override ? parseInt(body.plate_qty_override) : null;
      if (!body.plate_qty_override) delete body.plate_qty_override;
      await api.post(`/jobs/${editJob.id}/correct`, body);
      toast.success(`Job ${editJob.job_number} updated. Stock adjusted automatically.`);
      setEditJob(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingEdit(false); }
  };

  const openHistory = async (id) => {
    try {
      const { data } = await api.get(`/jobs/${id}/history`);
      setHistoryModal(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelJob = async (id) => {
    const reason = window.prompt("Cancellation reason?");
    if (!reason) return;
    try {
      await api.post(`/jobs/${id}/cancel`, { reason });
      toast.success("Job cancelled, stock reversed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const insufficient = stockHint != null && finalQty > stockHint;
  const fieldClass = (name) => fieldError?.field === name ? "ring-1 ring-red-500 border-red-500" : "";

  return (
    <div data-testid="jobs-page">
      <Toaster position="bottom-right" richColors />
      <PageHeader
        title="Job Sheet"
        subtitle="Every job auto-creates a linked consumption movement. The job name drives everything — problems in it must be fixed before saving."
        actions={<div className="flex flex-wrap gap-2">
          <Btn variant="outline" onClick={()=>window.location.assign("/jobs/bulk")} data-testid="job-bulk-btn">Bulk Import</Btn>
          <Btn data-testid="job-new-btn" onClick={openForm}><Plus size={14} /> New Job</Btn>
        </div>}
      />

      <div className="p-4 md:p-6 space-y-4 min-w-0">
        <Card>
          <div className="p-3 flex flex-wrap gap-3 items-end">
            <Field label="Client">
              <Select value={filter.client_id} onChange={e=>setFilter({...filter,client_id:e.target.value})}>
                <option value="">All</option>
                {m.clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Workshop">
              <Select value={filter.workshop} onChange={e=>setFilter({...filter,workshop:e.target.value})}>
                <option value="">All</option>
                {JOB_WORKSHOPS.map(x=><option key={x} value={x}>{x}</option>)}
              </Select>
            </Field>
            {!canManageAllWorkshops && filter.workshop && filter.workshop !== assignedWorkshop && (
              <div className="pb-1"><Chip tone="warn">View only: {filter.workshop}</Chip></div>
            )}
            <Field label="Baking">
              <Select value={filter.baking} onChange={e=>setFilter({...filter,baking:e.target.value})} data-testid="jobs-baking-filter">
                <option value="">All jobs</option>
                <option value="true">Baking only</option>
                <option value="false">Non-baking only</option>
              </Select>
            </Field>
            <Field label="From"><Input type="date" value={filter.from_date} onChange={e=>setFilter({...filter,from_date:e.target.value})} /></Field>
            <Field label="To"><Input type="date" value={filter.to_date} onChange={e=>setFilter({...filter,to_date:e.target.value})} /></Field>
            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-2 text-sm h-8 cursor-pointer select-none" data-testid="hide-cancelled-label">
                <input
                  type="checkbox"
                  checked={hideCancelled}
                  onChange={e => setHideCancelled(e.target.checked)}
                  data-testid="hide-cancelled-toggle"
                />
                <span className="font-semibold">Hide cancelled</span>
              </label>
            </div>
            <div className="w-full lg:w-auto lg:ml-auto flex flex-wrap gap-2 items-end">
              <Btn
                variant="outline"
                onClick={() => exportJobs("xlsx")}
                disabled={!filter.from_date || !filter.to_date || Boolean(exporting)}
                data-testid="jobs-export-xlsx"
                title="Select both From and To dates to export"
              >
                <FileXls size={15} /> {exporting === "xlsx" ? "Exporting…" : "Export Excel"}
              </Btn>
              <Btn
                variant="outline"
                onClick={() => exportJobs("pdf")}
                disabled={!filter.from_date || !filter.to_date || Boolean(exporting)}
                data-testid="jobs-export-pdf"
                title="Select both From and To dates to export"
              >
                <FilePdf size={15} /> {exporting === "pdf" ? "Exporting…" : "Export PDF"}
              </Btn>
            </div>
          </div>
        </Card>

        <Card>
          <div className="overflow-x-auto">
          <table className="w-full data-table min-w-[1100px]" data-testid="jobs-table">
            <thead><tr>
              <th className="px-3 py-2 text-left">Job No.</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Job / File</th>
              <th className="px-3 py-2 text-left">Client</th>
              <th className="px-3 py-2 text-left">Workshop</th>
              <th className="px-3 py-2 text-left">Size</th>
              <th className="px-3 py-2 text-center">S/C</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Challan</th>
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {visibleJobs.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-zinc-500 text-sm">No jobs. Click "New Job".</td></tr>}
              {visibleJobs.map(j => (
                <tr key={j.id} data-testid={`job-row-${j.id}`}>
                  <td className="px-3 py-2 font-mono font-semibold">{j.job_number}</td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">
                    <div>{j.job_date}</div>
                    <div className="mt-0.5 text-[10px] text-zinc-500">Entered {formatISTDateTime(j.created_at)}</div>
                  </td>
                  <td className="px-3 py-2"><div className="font-medium flex items-center gap-2">{j.job_name}{j.baking && <span className="rounded-full bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Baking</span>}</div><div className="text-[11px] text-zinc-500 font-mono">{j.file_name}</div></td>
                  <td className="px-3 py-2">{nameOf(m.clients, j.client_id)}</td>
                  <td className="px-3 py-2 font-mono">{j.workshop || "AMSKY"}</td>
                  <td className="px-3 py-2 font-mono">{sizeOf(m.plate_sizes, j.plate_size_id)}</td>
                  <td className="px-3 py-2 text-center font-mono">{j.sets}S{j.colours}C</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{j.final_plate_qty}</td>
                  <td className="px-3 py-2"><Chip tone={j.cancelled?"bad":j.status==="completed"?"good":j.status==="draft"?"warn":"info"}>{j.status}</Chip></td>
                    <td className="px-3 py-2 font-mono">{j.challan_number || "-"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={()=>openHistory(j.id)} className="text-xs text-sky-600 hover:text-sky-800 font-semibold underline underline-offset-2 mr-2" data-testid={`job-history-${j.id}`}>History</button>
                    {!j.cancelled && (["super_admin","admin","manager"].includes(user?.role) || (j.workshop || "AMSKY") === user?.assigned_workshop) && (
                      <Btn variant="ghost" onClick={() => openEdit(j)} data-testid={`job-edit-${j.id}`}>Edit</Btn>
                    )}
                    {!j.cancelled && j.status === "draft" && (["super_admin","admin","manager"].includes(user?.role) || (j.workshop || "AMSKY") === user?.assigned_workshop) && (
                      <Btn variant="primary" data-testid={`job-promote-${j.id}`} onClick={() => promote(j.id)}>Promote</Btn>
                    )}
                    {!j.cancelled && !j.challan_id && (["super_admin","admin","manager"].includes(user?.role) || (j.workshop || "AMSKY") === user?.assigned_workshop) && (
                      <Btn variant="ghost" data-testid={`job-cancel-${j.id}`} onClick={() => cancelJob(j.id)}>Cancel</Btn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="flex min-h-[100dvh] items-start sm:items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-sm border border-zinc-200 w-full max-w-4xl" onClick={e=>e.stopPropagation()} data-testid="job-form-dialog">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div>
                <div className="font-heading font-bold">New Job</div>
                <div className="text-[11px] text-zinc-500 font-mono mt-0.5">Filename format: JobName_Client_DPI_PlateSize_SetColour_Operator_Machine_Remark · The full portion before the client name becomes the Job Name.</div>
              </div>
              <button onClick={() => setShowForm(false)} data-testid="job-form-close"><X size={18} /></button>
            </div>
            <div className="p-4 sm:p-5 space-y-4 max-h-[78dvh] sm:max-h-[75vh] overflow-y-auto">
              {/* PDF upload — extracts thumbnail (for challan) and auto-fills the filename */}
              <div className="border border-dashed border-zinc-300 rounded-sm p-3 flex flex-col sm:flex-row sm:items-center gap-4 bg-zinc-50/50" data-testid="pdf-upload-panel">
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer border border-zinc-300 rounded-sm px-3 py-1.5 bg-white hover:bg-zinc-100">
                  <input type="file" accept=".pdf,application/pdf" className="hidden"
                    onChange={e => onPdfUpload(e.target.files?.[0])} data-testid="job-pdf-input" />
                  {uploadingPdf ? "Uploading…" : "Upload PDF"}
                </label>
                {thumbPreview ? (
                  <div className="flex items-center gap-2">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${thumbPreview}`} alt="thumbnail" className="w-14 h-14 object-cover border border-zinc-300 rounded-sm" data-testid="job-thumb-preview" />
                    <div className="text-[11px] text-zinc-600 leading-snug">
                      <div className="font-semibold text-zinc-900">Thumbnail generated</div>
                      <div>Will appear on the challan when this job is included.</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-zinc-600 leading-snug">
                    Upload the artwork PDF — the filename will auto-fill and the first page becomes the thumbnail printed on the challan.
                    <br/><span className="text-zinc-500">Or paste the filename manually below if you're not attaching a PDF.</span>
                  </div>
                )}
              </div>

              {/* Filename assist + parse report */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="md:col-span-2">
                  <Field label="Filename (paste to auto-fill & validate)">
                    <Input value={form.file_name} onChange={e=>onFilenameChange(e.target.value)} placeholder="Diwali-Card_AcmePrint_2400_550X700_5S2C_Ramesh_AMSKY_Rush.pdf" data-testid="job-filename-input" />
                  </Field>
                </div>
                <div className="text-[11px] text-zinc-500 leading-snug flex items-start gap-1.5">
                  <Info size={14} className="text-zinc-500 shrink-0 mt-0.5" />
                  Each segment is validated against Master Data. Problems shown below must be resolved before the job is saved.
                </div>
              </div>
              {parse && <ParseReport
                parse={parse}
                onQuickAddClient={quickAddClient}
                onQuickAddPlateSize={quickAddPlateSize}
                onQuickAddDpi={quickAddDpi}
                onQuickAddOperator={quickAddOperator}
              />}
              {fieldError && (
                <div className="border border-red-300 bg-red-50 rounded-sm p-3 flex items-start gap-2" data-testid="field-error-banner">
                  <XCircle size={18} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-red-900">Problem: {fieldError.problem}</div>
                    {fieldError.resolution && <div className="text-red-800 mt-1">How to fix: {fieldError.resolution}</div>}
                    {fieldError.field && <div className="text-[11px] text-red-700 mt-1 font-mono">field: {fieldError.field}</div>}
                  </div>
                </div>
              )}

              {/* Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Job Name*">
                  <Input
                    value={form.job_name}
                    onChange={e => {
                      jobNameEdited.current = true;
                      setForm({...form, job_name: e.target.value});
                    }}
                    data-testid="job-name-input"
                    className={fieldClass("job_name")}
                  />
                </Field>
                <Field label="Job Date*"><Input type="date" value={form.job_date} onChange={e=>setForm({...form,job_date:e.target.value})} data-testid="job-date-input" /></Field>
                <Field label="Baking*">
                  <Select value={form.baking ? "yes" : "no"} onChange={e=>setForm({...form,baking:e.target.value === "yes"})} data-testid="job-baking-select">
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Select>
                </Field>
                <Field label="Client*">
                  <Select value={form.client_id} onChange={e=>onClientChange(e.target.value)} data-testid="job-client-select" className={fieldClass("client_id")}>
                    <option value="">— Select —</option>
                    {m.clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
                <Field label="Machine Name*">
                  <Select
                    value={form.location_id}
                    onChange={e => {
                      const loc_id = e.target.value;
                      const loc = m.locations.find(l => l.id === loc_id);
                      const mc = loc ? m.machines.find(x => x.name.toLowerCase() === loc.name.toLowerCase()) : null;
                      const pu = loc ? m.production_units.find(x => x.name.toLowerCase() === loc.name.toLowerCase()) : null;
                      setForm({ ...form, location_id: loc_id, workshop: loc?.name?.toUpperCase() || "", machine_id: mc?.id || "", production_unit_id: pu?.id || "" });
                    }}
                    data-testid="job-location-select"
                    className={fieldClass("location_id") + " " + fieldClass("machine_id")}
                  >
                    <option value="">— Select —</option>
                    {m.locations.filter(x => x.active !== false && JOB_WORKSHOPS.includes(x.name?.toUpperCase()) && (["super_admin","admin","manager"].includes(user?.role) || x.name?.toUpperCase() === user?.assigned_workshop)).map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                  </Select>
                </Field>
                <Field label="Plate Size*">
                  <div className="space-y-1.5">
                    <Select value={form.plate_size_id} onChange={e=>onPlateSizeChange(e.target.value)} data-testid="job-size-select" className={fieldClass("plate_size_id")}>
                      <option value="">— Select —</option>
                      {historyApplies ? (
                        <>
                          <optgroup label={`Usual for ${clientName}`}>
                            {clientSizeHistory.sizes.map((item, index)=><option key={item.plate_size_id} value={item.plate_size_id}>{item.canonical}{index === 0 ? " — most used" : ""}</option>)}
                          </optgroup>
                          <optgroup label="Other plate sizes">
                            {m.plate_sizes.filter(x=>!usualSizeIds.includes(x.id)).map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
                          </optgroup>
                        </>
                      ) : m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
                    </Select>
                    {clientSizeHistory.loading && form.client_id && (
                      <div className="text-[11px] text-zinc-500">Checking this client's previous plate sizes…</div>
                    )}
                    {historyApplies && !selectedPlateIsNew && (
                      <div className="text-[11px] text-emerald-700" data-testid="client-usual-sizes">
                        Usual sizes: {clientSizeHistory.sizes.map(x=>x.canonical).join(", ")}. The most used size is prefilled.
                      </div>
                    )}
                    {selectedPlateIsNew && (
                      <div className="text-[11px] text-amber-800 font-semibold" data-testid="client-new-size-warning">
                        New size for this client — confirmation is required before saving.
                      </div>
                    )}
                  </div>
                </Field>
                <Field label="Operator">
                  <Select value={form.operator_id} onChange={e=>setForm({...form,operator_id:e.target.value})}>
                    <option value="">—</option>
                    {m.operators.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                  </Select>
                </Field>
                <Field label="DPI">
                  <div className="flex gap-1">
                    <Select value={form.dpi} onChange={e=>{
                      if (e.target.value === "__add__") {
                        const val = window.prompt("Enter new DPI value:");
                        if (val && !isNaN(parseInt(val))) {
                          api.post("/masters/dpis", { value: parseInt(val), active: true })
                            .then(() => { toast.success(`DPI ${val} added`); m.reload(); setForm(f => ({...f, dpi: val})); })
                            .catch(err => toast.error(formatApiError(err)));
                        }
                      } else {
                        setForm({...form, dpi: e.target.value});
                      }
                    }} className="flex-1">
                      <option value="">— Select —</option>
                      {(m.dpis||[]).filter(d => d.active !== false).map(d => <option key={d.id} value={d.value}>{d.value}</option>)}
                      <option value="__add__">+ Add new DPI…</option>
                    </Select>
                  </div>
                </Field>
                <Field label="Sets*"><Input type="number" min="1" value={form.sets} onChange={e=>setForm({...form,sets:e.target.value})} data-testid="job-sets-input" className={fieldClass("sets_colours")} /></Field>
                <Field label="Colours*"><Input type="number" min="1" value={form.colours} onChange={e=>setForm({...form,colours:e.target.value})} data-testid="job-colours-input" className={fieldClass("sets_colours")} /></Field>
                <Field label="Override Qty">
                  <Input type="number" value={form.plate_qty_override} onChange={e=>setForm({...form,plate_qty_override:e.target.value})} placeholder={String((parseInt(form.sets)||0)*(parseInt(form.colours)||0))} className={fieldClass("plate_qty_override")} />
                </Field>
                {form.plate_qty_override && (
                  <Field label="Override Reason*"><Input value={form.override_reason} onChange={e=>setForm({...form,override_reason:e.target.value})} className={fieldClass("override_reason")} /></Field>
                )}
                <div className="col-span-full">
                  <Field label="Remarks"><Input value={form.remarks} onChange={e=>setForm({...form,remarks:e.target.value})} /></Field>
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-3 flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-zinc-500">Final plates: </span>
                  <span className="font-mono font-bold text-lg" data-testid="job-final-qty">{finalQty}</span>
                  {stockHint != null && (
                    <span className={`ml-4 font-mono ${insufficient?"text-red-600 font-bold":"text-zinc-600"}`} data-testid="job-stock-hint">
                      Available: {stockHint} {insufficient && "⚠ INSUFFICIENT"}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <Btn variant="outline" onClick={() => setShowForm(false)}>Cancel</Btn>
              <Btn variant="outline" onClick={() => submit("draft")} data-testid="job-save-draft-btn">Save as Draft</Btn>
              <Btn onClick={() => submit("submitted")} data-testid="job-submit-btn">Submit Job</Btn>
            </div>
          </div>
          </div>
        </div>
      )}

      {editJob && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={()=>!savingEdit && setEditJob(null)}>
          <div className="flex min-h-[100dvh] items-start sm:items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-sm border border-zinc-200 w-full max-w-2xl" onClick={e=>e.stopPropagation()} data-testid="job-edit-dialog">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div>
                <div className="font-heading font-bold">Edit Job — {editJob.job_number}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{editJob.job_name} · Changing quantity/machine will automatically reverse the old stock movement and record the new one.</div>
                {editJob.challan_id && (
                  <div className="mt-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="edit-challaned-warning">
                    <WarningCircle size={14} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      This job is on challan <span className="font-mono font-bold">{editJob.challan_number}</span>. Only admins can correct challaned jobs. The parent challan's total plates will be automatically recomputed. Re-print the challan afterwards to reflect the new totals.
                    </div>
                  </div>
                )}
              </div>
              <button onClick={()=>setEditJob(null)}><X size={18} /></button>
            </div>
            <div className="p-4 sm:p-5 space-y-3 max-h-[78dvh] sm:max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <Field label="Job Date"><Input type="date" value={editForm.job_date} onChange={e=>setEditForm({...editForm,job_date:e.target.value})} data-testid="edit-job-date" /></Field>
                <Field label="Baking">
                  <Select value={editForm.baking ? "yes" : "no"} onChange={e=>setEditForm({...editForm,baking:e.target.value === "yes"})} data-testid="edit-baking-select">
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Select>
                </Field>
                <Field label="Machine (Workshop)">
                  <Select value={editForm.location_id} onChange={e => {
                    const loc = m.locations.find(l => l.id === e.target.value);
                    const mc = loc ? m.machines.find(x => x.name.toLowerCase() === loc.name.toLowerCase()) : null;
                    setEditForm({...editForm, location_id: e.target.value, workshop: loc?.name?.toUpperCase() || "", machine_id: mc?.id || ""});
                  }} data-testid="edit-location-select">
                    {m.locations.filter(x => x.active !== false && JOB_WORKSHOPS.includes(x.name?.toUpperCase()) && (["super_admin","admin","manager"].includes(user?.role) || x.name?.toUpperCase() === user?.assigned_workshop)).map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                  </Select>
                </Field>
                <Field label="Plate Size">
                  <Select value={editForm.plate_size_id} onChange={e=>setEditForm({...editForm,plate_size_id:e.target.value})} data-testid="edit-plate-size">
                    {m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
                  </Select>
                </Field>
                <Field label="Sets"><Input type="number" min="1" value={editForm.sets} onChange={e=>setEditForm({...editForm,sets:e.target.value})} data-testid="edit-sets" /></Field>
                <Field label="Colours"><Input type="number" min="1" value={editForm.colours} onChange={e=>setEditForm({...editForm,colours:e.target.value})} data-testid="edit-colours" /></Field>
                <Field label="Override Qty">
                  <Input type="number" value={editForm.plate_qty_override} onChange={e=>setEditForm({...editForm,plate_qty_override:e.target.value})} placeholder={String((parseInt(editForm.sets)||0)*(parseInt(editForm.colours)||0))} data-testid="edit-override-qty" />
                </Field>
                <div className="col-span-full">
                  <Field label="Remarks"><Input value={editForm.remarks} onChange={e=>setEditForm({...editForm,remarks:e.target.value})} /></Field>
                </div>
                <div className="col-span-full">
                  <Field label="Reason for correction* (audit log)">
                    <Input value={editForm.reason} onChange={e=>setEditForm({...editForm,reason:e.target.value})} placeholder="e.g. Wrong plate size entered, correcting to actual" data-testid="edit-reason" />
                  </Field>
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 leading-snug bg-zinc-50 border border-zinc-200 rounded-sm p-2">
                Editing a submitted job writes an audit correction — old plate consumption is reversed and new consumption is recorded on the current stock. History is preserved.
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <Btn variant="outline" onClick={()=>setEditJob(null)} disabled={savingEdit}>Cancel</Btn>
              <Btn onClick={saveEdit} disabled={savingEdit} data-testid="edit-save-btn">{savingEdit ? "Saving…" : "Save Changes"}</Btn>
            </div>
          </div>
          </div>
        </div>
      )}

      {historyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto" onClick={()=>setHistoryModal(null)}>
          <div className="flex min-h-[100dvh] items-start sm:items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-sm border border-zinc-200 w-full max-w-2xl" onClick={e=>e.stopPropagation()} data-testid="job-history-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200">
              <div>
                <div className="font-heading font-bold">Job History — {historyModal.job.job_number}</div>
                <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{historyModal.job.job_name} · {historyModal.job.job_date}</div>
              </div>
              <button onClick={()=>setHistoryModal(null)}><X size={18} /></button>
            </div>
            <div className="p-4 sm:p-5 max-h-[78dvh] sm:max-h-[70vh] overflow-y-auto">
              {historyModal.events.length === 0 && <div className="text-sm text-zinc-500">No events recorded.</div>}
              <ol className="relative border-l-2 border-zinc-200 ml-3 space-y-4">
                {historyModal.events.map((e, i) => (
                  <li key={`${e.kind}-${e.id || e.at || i}`} className="ml-4">
                    <div className={`absolute w-3 h-3 rounded-full -left-[7px] ${
                      e.kind==="movement" && e.movement_type==="job_consumption" ? "bg-red-500" :
                      e.kind==="movement" && e.movement_type==="consumption_reversal" ? "bg-emerald-500" :
                      e.kind==="challan" ? "bg-sky-500" : "bg-zinc-400"
                    }`}></div>
                    <div className="text-[11px] font-mono text-zinc-500">{(e.at||"").slice(0,19).replace("T"," ")}</div>
                    {e.kind === "audit" && (
                      <div className="text-sm">
                        <span className="font-semibold">{e.action}</span> in <span className="font-mono">{e.module}</span> by <span className="font-mono">{e.user}</span>
                        {e.reason && <div className="text-[12px] text-zinc-600 mt-0.5">Reason: {e.reason}</div>}
                      </div>
                    )}
                    {e.kind === "movement" && (
                      <div className="text-sm">
                        <Chip tone={e.signed_quantity < 0 ? "bad" : "good"}>{e.movement_type}</Chip>
                        <span className="ml-2 font-mono">{e.signed_quantity > 0 ? "+" : ""}{e.signed_quantity} plates</span>
                        <span className="ml-2 text-[11px] text-zinc-500">on {e.movement_date} by {e.user}</span>
                        {e.remarks && <div className="text-[12px] text-zinc-600 mt-0.5 leading-snug">{e.remarks}</div>}
                      </div>
                    )}
                    {e.kind === "challan" && (
                      <div className="text-sm">
                        <Chip tone={e.cancelled ? "bad" : "info"}>Challan {e.cancelled ? "cancelled" : "generated"}</Chip>
                        <span className="ml-2 font-mono font-bold">{e.challan_number}</span>
                        <span className="ml-2 text-[11px] text-zinc-500">{e.challan_date} by {e.user}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
            <div className="flex justify-end px-5 py-3 border-t border-zinc-200">
              <Btn variant="outline" onClick={()=>setHistoryModal(null)}>Close</Btn>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
